"use client";

import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { textureFromCanvas } from "../textures";

// The landing scene's Memory Fabric state, held rather than scrubbed: the
// hardware stays a blueprint, ringed by the code stream. This page is an
// analytics surface, so the object idles instead of riding the scroll.
//
// It renders as the hero's background band with copy on top, so everything is
// pitched well below the landing's levels - the fabric has to stay a backdrop.

type NetworkUsageSceneProps = {
  // Relative pressure of the trailing 24h window, 0..1. Drives the density of
  // the code stream only - the scene never claims to be real-time telemetry.
  intensity?: number;
};

const clamp = (value: number, min = 0, max = 1) => Math.max(min, Math.min(max, value));
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

const FADE_DELAY = 260;
const FADE_DURATION = 2200;

const CODE_CHARS = [
  "0", "1", "2", "3", "4", "5", "6", "7", "8", "9",
  "A", "B", "C", "D", "E", "F", "X", "[", "]", "<", ">", ":", "/", "G", "P", "U"
];

// Monospace glyph atlas for the code stream: one texture, one draw call.
function buildCodeAtlas() {
  const charSize = 128;
  const canvas = document.createElement("canvas");
  canvas.width = charSize * CODE_CHARS.length;
  canvas.height = charSize;

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (ctx) {
    ctx.font = "bold 80px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#ffffff";
    CODE_CHARS.forEach((char, i) => ctx.fillText(char, (i * charSize) + (charSize / 2), charSize / 2));
  }

  return textureFromCanvas(canvas, ctx);
}

// Radial halo behind the object: the bloom pass alone leaves the blueprint
// floating in flat black.
function buildGlowTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 256;

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (ctx) {
    const gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    gradient.addColorStop(0, "rgba(255,255,255,1)");
    gradient.addColorStop(0.12, "rgba(34,211,238,0.9)");
    gradient.addColorStop(0.35, "rgba(0,100,255,0.35)");
    gradient.addColorStop(0.6, "rgba(0,30,150,0.1)");
    gradient.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 256, 256);
  }
  // The halo is a radial gradient, so top-down sampling is indistinguishable
  // from bottom-up: it needs no counterpart to the atlas's flip handling.
  return textureFromCanvas(canvas, ctx);
}

// The blueprint look replaces every material the GLB ships with, so the
// originals - and the textures they hold - have to be handed back, or they stay
// resident on the GPU for as long as the page lives.
function releaseMaterial(material: THREE.Material | THREE.Material[]) {
  const entries = Array.isArray(material) ? material : [ material ];
  entries.forEach((entry) => {
    Object.values(entry as unknown as Record<string, unknown>).forEach((value) => {
      if ((value as THREE.Texture)?.isTexture) (value as THREE.Texture).dispose();
    });
    entry.dispose();
  });
}

const CODE_VERTEX = `
  attribute float aRandom;
  varying float vRandom;
  uniform float uSize;
  void main() {
    vRandom = aRandom;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = uSize / -mvPosition.z;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const CODE_FRAGMENT = `
  uniform sampler2D uTexture;
  uniform float uTime;
  uniform float uAtlasSize;
  uniform float uStringLen;
  uniform float uOpacity;
  varying float vRandom;
  void main() {
    float yMin = 0.42; float yMax = 0.58;
    if (gl_PointCoord.y < yMin || gl_PointCoord.y > yMax) discard;
    float charIndex = floor(gl_PointCoord.x * uStringLen);
    float localUvX = fract(gl_PointCoord.x * uStringLen);
    // gl_PointCoord runs top-down and so does the atlas buffer - no flip.
    float localUvY = (gl_PointCoord.y - yMin) / (yMax - yMin);
    float timeStep = floor(uTime * 4.0 + vRandom * 100.0);
    float charFrame = floor(mod(timeStep, uAtlasSize));
    vec4 texColor = texture2D(uTexture, vec2((charFrame + localUvX) / uAtlasSize, localUvY));
    float edgeFade = (charIndex < 1.0 || charIndex > uStringLen - 2.0) ? 0.5 : 1.0;
    gl_FragColor = vec4(vec3(0.25, 1.1, 1.4) * texColor.rgb, texColor.a * uOpacity * edgeFade);
  }
`;

export default function NetworkUsageScene({ intensity = .4 }: NetworkUsageSceneProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const mobile = window.innerWidth < 768;
    const flow = clamp(.28 + intensity * .72);
    const hero = canvas.closest<HTMLElement>(".network-usage__hero");

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
    camera.position.set(0, 0, 10);

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: !mobile, powerPreference: "high-performance" });
    const pixelRatio = mobile ? 1 : Math.min(window.devicePixelRatio, 1.75);
    renderer.setPixelRatio(pixelRatio);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;

    // Bloom is what turns bare edges into the Memory Fabric look. Mobile pays
    // too much for it, so it renders the raw pass instead.
    // The composer is sized in device pixels, taken from the drawing buffer, so
    // its ratio stays 1: letting it apply the pixel ratio itself would hand the
    // render targets a fractional size the canvas has already floored.
    const composer = new EffectComposer(renderer);
    composer.setPixelRatio(1);
    composer.addPass(new RenderPass(scene, camera));

    let bloomPass: UnrealBloomPass | null = null;
    if (!mobile) {
      bloomPass = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.85, 0.45, 0.7);
      composer.addPass(bloomPass);
    }

    const root = new THREE.Group();
    scene.add(root);

    const glowTexture = buildGlowTexture();
    const glowMaterial = new THREE.SpriteMaterial({
      map: glowTexture,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    const glow = new THREE.Sprite(glowMaterial);
    glow.position.set(0, 0, -3);
    scene.add(glow);

    // Code stream: a ring of glyph strings orbiting the blueprint. Density
    // follows the 24h pressure.
    const codeCount = mobile ? 22 : 56;
    const codePositions = new Float32Array(codeCount * 3);
    const codeRandoms = new Float32Array(codeCount);
    for (let i = 0; i < codeCount; i += 1) {
      const radius = mobile ? 2.5 : 3.5;
      const angle = Math.random() * Math.PI * 2;
      codePositions[i * 3] = Math.cos(angle) * radius;
      codePositions[i * 3 + 1] = (Math.random() - .5) * 2;
      codePositions[i * 3 + 2] = Math.sin(angle) * radius;
      codeRandoms[i] = Math.random();
    }

    const codeAtlas = buildCodeAtlas();
    const codeGeometry = new THREE.BufferGeometry();
    codeGeometry.setAttribute("position", new THREE.BufferAttribute(codePositions, 3));
    codeGeometry.setAttribute("aRandom", new THREE.BufferAttribute(codeRandoms, 1));
    const codeMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uTexture: { value: codeAtlas },
        uAtlasSize: { value: CODE_CHARS.length },
        uStringLen: { value: 10.0 },
        uOpacity: { value: 0 },
        uSize: { value: mobile ? 500.0 : 900.0 }
      },
      vertexShader: CODE_VERTEX,
      fragmentShader: CODE_FRAGMENT,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    const codeStream = new THREE.Points(codeGeometry, codeMaterial);
    root.add(codeStream);

    let fabric: THREE.Group | null = null;
    let fabricSpan = 0;
    let disposed = false;
    let fadeStart = 0;
    let progress = reducedMotion ? 1 : 0;
    let publishedProgress = -1;
    let appliedProgress = -1;

    const lineMaterials: THREE.LineBasicMaterial[] = [];
    const shellMaterials: THREE.MeshBasicMaterial[] = [];

    const applyProgress = (p: number) => {
      appliedProgress = p;
      lineMaterials.forEach((material) => { material.opacity = p * .34; });
      shellMaterials.forEach((material) => { material.opacity = p * .08; });
      // The stream is a backdrop, but at the old .085 an idle network pushed it
      // to ~.06 - under the hero copy that read as nothing at all.
      codeMaterial.uniforms.uOpacity.value = p * (mobile ? .095 : .16) * (.5 + flow * .8);
      glowMaterial.opacity = p * .11;
      if (bloomPass) bloomPass.strength = .34 + p * .26;
    };
    applyProgress(progress);

    const loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
    loader.load(mobile ? "/landing-assets/GPU_mobile.glb" : "/landing-assets/GPU.glb", (gltf) => {
      if (disposed) return;

      const model = gltf.scene;
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      model.position.set(-center.x, -center.y, -center.z);

      model.traverse((child) => {
        if (!(child as THREE.Mesh).isMesh) return;
        const mesh = child as THREE.Mesh;

        const lineMaterial = new THREE.LineBasicMaterial({
          color: 0x00aaff,
          transparent: true,
          opacity: 0,
          blending: THREE.AdditiveBlending,
          depthWrite: false
        });
        const edges = new THREE.LineSegments(new THREE.EdgesGeometry(mesh.geometry, 15), lineMaterial);
        edges.renderOrder = 10;
        mesh.add(edges);
        lineMaterials.push(lineMaterial);

        const shellMaterial = new THREE.MeshBasicMaterial({
          color: 0x003366,
          wireframe: true,
          transparent: true,
          opacity: 0,
          blending: THREE.AdditiveBlending,
          depthWrite: false
        });
        releaseMaterial(mesh.material);
        mesh.material = shellMaterial;
        shellMaterials.push(shellMaterial);
      });

      model.rotation.z -= Math.PI;

      fabric = new THREE.Group();
      fabric.add(model);
      fabricSpan = Math.max(size.x, size.y, size.z);
      fitFabric();
      // Slight three-quarter view: dead-frontal reads as a flat diagram.
      fabric.rotation.set(.1, -.18, 0);
      root.add(fabric);

      applyProgress(progress);

      if (reducedMotion) {
        progress = 1;
        applyProgress(1);
        hero?.style.setProperty("--hero-progress", "1");
        composer.render();
      }
    });

    // The hero band is far wider than it is tall on desktop and nearly square on
    // a phone. A fixed world size would fill a narrow canvas edge to edge and
    // read as noise behind the copy, so the object is fitted to the viewport.
    const fitFabric = () => {
      if (!fabric || !fabricSpan) return;

      const visibleHeight = 2 * Math.tan((camera.fov * Math.PI / 180) / 2) * camera.position.z;
      const visibleWidth = visibleHeight * camera.aspect;
      fabric.scale.setScalar(Math.min(visibleWidth * .58, 8.5) / fabricSpan);
    };

    const bufferSize = new THREE.Vector2();

    const resize = () => {
      // Whole pixels only. The hero is a grid cell, so its measured size is
      // fractional, and at a 1.75 ratio the device size is fractional again -
      // leaving the drawing buffer (floored by three) and the composer's render
      // targets (not floored) disagreeing by a row. The buffer is asked for its
      // own size and the composer is handed exactly that.
      const rect = canvas.getBoundingClientRect();
      const width = Math.round(rect.width);
      const height = Math.round(rect.height);
      if (!width || !height) return;

      renderer.setSize(width, height, false);
      // setSize resizes every pass, the bloom pyramid included - the pass needs
      // nothing set by hand.
      renderer.getDrawingBufferSize(bufferSize);
      composer.setSize(bufferSize.x, bufferSize.y);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      fitFabric();
      if (reducedMotion) composer.render();
    };
    resize();

    // The canvas is a grid cell: it resizes without the window resizing.
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);

    const clock = new THREE.Clock();
    let raf = 0;
    let running = false;

    const animate = () => {
      const elapsed = clock.getElapsedTime();

      if (progress < 1) {
        if (!fadeStart) fadeStart = performance.now();
        progress = easeOut(clamp((performance.now() - fadeStart - FADE_DELAY) / FADE_DURATION));
      }
      // The reveal settles at 1 and stays there: past that point rewriting every
      // material's opacity each frame buys nothing.
      if (progress !== appliedProgress) applyProgress(progress);

      // Publish the reveal for the DOM caption without forcing layout: one CSS
      // variable, only when it actually moved.
      if (hero && Math.abs(progress - publishedProgress) > .004) {
        publishedProgress = progress;
        hero.style.setProperty("--hero-progress", progress.toFixed(3));
      }

      codeMaterial.uniforms.uTime.value = elapsed;
      codeStream.rotation.y = elapsed * .15;
      codeStream.rotation.z = Math.sin(elapsed * .4) * .15;
      codeStream.position.y = Math.sin(elapsed * 1.2) * .12;

      if (fabric) {
        fabric.rotation.y = -.18 + Math.sin(elapsed * .18) * .16;
        fabric.rotation.x = .1 + Math.sin(elapsed * .13) * .04;
        fabric.position.y = Math.sin(elapsed * .5) * .14;
      }

      const pulse = 1 + Math.sin(elapsed * .8) * .05;
      glow.scale.set((mobile ? 4 : 6.5) * pulse, (mobile ? 4 : 6.5) * pulse, 1);
      glow.position.y = fabric ? fabric.position.y : 0;

      composer.render();
      raf = requestAnimationFrame(animate);
    };

    // Off-screen frames are wasted GPU time on a page that scrolls past the hero.
    const visibility = new IntersectionObserver(([ entry ]) => {
      const visible = entry.isIntersecting;
      if (visible === running || reducedMotion) return;

      running = visible;
      if (running) {
        raf = requestAnimationFrame(animate);
      } else {
        cancelAnimationFrame(raf);
      }
    }, { threshold: 0.01 });
    visibility.observe(canvas);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      visibility.disconnect();
      resizeObserver.disconnect();
      fabric?.traverse((child) => {
        const mesh = child as THREE.Mesh;
        mesh.geometry?.dispose();
      });
      lineMaterials.forEach((material) => material.dispose());
      shellMaterials.forEach((material) => material.dispose());
      codeGeometry.dispose();
      codeMaterial.dispose();
      codeAtlas.dispose();
      glowTexture.dispose();
      glowMaterial.dispose();
      bloomPass?.dispose();
      composer.dispose();
      renderer.dispose();
      // Turbo unmounts this on every navigation away. dispose() frees the GPU
      // objects but leaves the context itself alive, and Safari caps a page at
      // roughly a dozen - without this, browsing back and forth eventually
      // costs the page its oldest live context.
      renderer.forceContextLoss();
    };
  }, [ intensity ]);

  return <canvas className="network-usage__three-canvas" ref={canvasRef} />;
}
